import { useMemo, useState } from "react";
import { Plus, Trash2, Copy, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, useTitle } from "@/components/PageHeader";
import { useSiteConfig } from "@/context/SiteConfig";
import { IMAGES } from "@/data/content";

// RedM text color codes. Preview hex is approximate — the code is what gets inserted verbatim.
const COLORS = [
  { code: "~q~", name: "Red", hex: "#c0392b" },
  { code: "~t7~", name: "Teal", hex: "#2DD4BF" },
  { code: "~e~", name: "Green", hex: "#27ae60" },
  { code: "~y~", name: "Yellow", hex: "#f1c40f" },
  { code: "~o~", name: "Orange", hex: "#e67e22" },
  { code: "~b~", name: "Blue", hex: "#3498db" },
  { code: "~p~", name: "Purple", hex: "#9b59b6" },
  { code: "~w~", name: "White", hex: "#ffffff" },
  { code: "~m~", name: "Grey", hex: "#95a5a6" },
];

const HOW_TO = [
  "Type in the scene description for each line (start with 5 lines, add more as needed).",
  "Select a color and size for each line (max 100px).",
  'Click "Add line" if you need more than 5 lines.',
  'The code builds automatically on the right — then click "Copy code".',
  "In game, type /scene and PASTE the copied code!",
  "Position the scene using the in-game commands.",
];

const hexFor = (code) => COLORS.find((c) => c.code === code)?.hex || "#f0e6d2";
let uid = 0;
const blankLine = () => ({ id: ++uid, text: "", color: "~w~", size: 20 });

export default function SceneHelper() {
  useTitle("Scene Helper | Aurora Springs RP | RedM Roleplay", "Plan richer roleplay scenes with the Aurora Springs RP scene helper — a creative tool for players on our 1895 RedM roleplay server.");
  const { img } = useSiteConfig();
  const [lines, setLines] = useState(() => Array.from({ length: 5 }, blankLine));
  const [copied, setCopied] = useState(false);

  const output = useMemo(
    () => lines
      .filter((l) => l.text.trim() !== "")
      .map((l) => `<font color="${hexFor(l.color)}" size="${l.size}">${l.text}~n~</font>`)
      .join(""),
    [lines]
  );

  const patch = (id, key, val) => setLines((p) => p.map((l) => (l.id === id ? { ...l, [key]: val } : l)));
  const add = () => setLines((p) => [...p, blankLine()]);
  const remove = (id) => setLines((p) => p.filter((l) => l.id !== id));
  const copy = async () => { await navigator.clipboard.writeText(output); setCopied(true); toast.success("Scene text copied"); setTimeout(() => setCopied(false), 1800); };

  return (
    <>
      <PageHeader overline="RP Tools" title="Scene Helper" image={img("upload", IMAGES.aurora)}
        sub="Create multi-line RedM scene text with custom colors and font sizes, then copy the code straight into game." />

      {/* How to Use */}
      <section className="mx-auto max-w-5xl px-6 sm:px-10 pt-14">
        <div className="border border-[#2A201A] bg-surface/40 p-6" data-testid="scene-howto">
          <p className="flex items-center gap-2 font-feature text-xs uppercase tracking-[0.25em] text-teal mb-4"><Info className="h-4 w-4" /> How to Use</p>
          <ol className="space-y-2.5">
            {HOW_TO.map((step, i) => (
              <li key={i} className="flex gap-3 text-[15px] text-[#b8b0a4] leading-relaxed">
                <span className="shrink-0 font-mono text-xs text-teal mt-1">{String(i + 1).padStart(2, "0")}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 sm:px-10 py-12 grid gap-10 lg:grid-cols-2 items-start">
        {/* Builder */}
        <div className="space-y-4">
          <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal">Lines</p>
          {lines.map((l) => (
            <div key={l.id} data-testid={`scene-line-${l.id}`} className="border border-[#2A201A] bg-surface/40 p-4 space-y-3">
              <input value={l.text} onChange={(e) => patch(l.id, "text", e.target.value)} placeholder="Line text…"
                className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal" />
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex gap-1.5">
                  {COLORS.map((c) => (
                    <button key={c.code} onClick={() => patch(l.id, "color", c.code)} title={`${c.name} ${c.code}`}
                      className={`h-7 w-7 border transition-transform ${l.color === c.code ? "border-bone scale-110" : "border-[#2A201A]"}`}
                      style={{ backgroundColor: c.hex }} />
                  ))}
                </div>
                <label className="flex items-center gap-2 font-mono text-xs text-[#8F877C]">
                  size
                  <input type="number" min="8" max="100" value={l.size} onChange={(e) => patch(l.id, "size", Math.min(100, Number(e.target.value) || 18))}
                    className="w-16 bg-obsidian border border-[#2A201A] px-2 py-1 text-bone focus:outline-none focus:border-teal" />
                </label>
                <button onClick={() => remove(l.id)} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          <button onClick={add} data-testid="scene-add-line" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-4 py-3 hover:border-teal hover:text-teal transition-colors"><Plus className="h-4 w-4" /> Add line</button>
        </div>

        {/* Preview + Output */}
        <div className="lg:sticky lg:top-28 space-y-6">
          <div>
            <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-3">Preview</p>
            <div className="border border-[#2A201A] bg-obsidian p-8 min-h-[160px] flex flex-col items-center justify-center gap-1" data-testid="scene-preview">
              {lines.filter((l) => l.text.trim() !== "").map((l) => (
                <span key={l.id} style={{ color: hexFor(l.color), fontSize: `${Math.max(12, Math.min(100, l.size))}px`, lineHeight: 1.2, textShadow: "0 1px 4px rgba(0,0,0,0.8)" }} className="text-center">
                  {l.text}
                </span>
              ))}
              {output === "" && <span className="font-mono text-xs text-[#5c554c]">Type some line text to see a preview…</span>}
            </div>
            <p className="mt-2 font-mono text-[10px] text-[#5c554c]">This preview matches the exact color your scene text will show in game.</p>
          </div>
          <div>
            <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal mb-3">Output code</p>
            <textarea readOnly value={output} data-testid="scene-output" rows={4} placeholder="Your scene code appears here…"
              className="w-full bg-obsidian border border-[#2A201A] px-4 py-3 font-mono text-xs text-bone focus:outline-none focus:border-teal resize-none" />
            <button onClick={copy} disabled={output === ""} data-testid="scene-copy-btn"
              className="mt-3 inline-flex items-center gap-2 font-feature text-sm uppercase tracking-widest bg-teal text-obsidian px-6 py-3 hover:bg-bone transition-colors disabled:opacity-50">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy code"}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
